<template>
  <div id="container" class="platform-application-page">
    <div class="platform-application-frame-wrap">
      <iframe
        id="platformApplicationFrame"
        ref="platformApplicationFrame"
        :src="iframeUrl"
        :style="frameHeight ? { height: frameHeight + 'px' } : null"
        :title="applicationName + ' 활용신청'"
        frameborder="0"
        allow="clipboard-write"
      ></iframe>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

// 전역 인터셉터의 400·408 페이지 이동 없이 인증 상태만 확인한다.
const portalAuthClient = axios.create({ headers: { 'X-Requested-With': 'XMLHttpRequest' } })

export default {
  name: 'PlatformApplication',
  data () {
    return {
      frameHeight: null,
      authCheckVersion: 0
    }
  },
  props: {
    applicationName: {
      type: String,
      required: true
    },
    iframeUrl: {
      type: String,
      required: true
    }
  },
  mounted () {
    window.addEventListener('message', this.handleIframeMessage)

    const iframe = this.$refs.platformApplicationFrame
    iframe.addEventListener('load', this.initializeIframe)
    window.addEventListener('resize', this.initializeIframe)
    window.addEventListener('scroll', this.scheduleViewport, { passive: true, capture: true })

    const header = document.getElementById('header')

    if (!header) {
      return
    }

    this.headerClassObserver = new MutationObserver(() => {
      if (!header.classList.contains('scroll')) {
        header.classList.add('scroll')
      }
    })
    this.headerClassObserver.observe(header, {
      attributes: true,
      attributeFilter: ['class']
    })
    header.classList.add('scroll')
  },
  beforeDestroy () {
    this.authCheckVersion++
    const header = document.getElementById('header')
    const iframe = this.$refs.platformApplicationFrame

    window.removeEventListener('message', this.handleIframeMessage)
    window.removeEventListener('resize', this.initializeIframe)
    window.removeEventListener('scroll', this.scheduleViewport, true)
    window.cancelAnimationFrame(this.viewportFrame)
    if (iframe) {
      iframe.removeEventListener('load', this.initializeIframe)
    }
    if (this.headerClassObserver) {
      this.headerClassObserver.disconnect()
    }
    if (header) {
      header.classList.remove('scroll')
    }
  },
  methods: {
    scheduleViewport () {
      if (this.viewportFrame) return
      this.viewportFrame = window.requestAnimationFrame(() => {
        this.viewportFrame = null
        this.sendViewport()
      })
    },
    sendViewport () {
      const iframe = this.$refs.platformApplicationFrame
      const origin = this.getIframeOrigin()
      if (!iframe || !iframe.contentWindow || !origin) return
      const rect = iframe.getBoundingClientRect()
      const header = document.getElementById('header')
      const visibleTop = Math.max(0, rect.top, header ? header.getBoundingClientRect().bottom : 0)
      iframe.contentWindow.postMessage({
        type: 'darp:viewport',
        top: Math.max(0, visibleTop - rect.top),
        height: Math.max(0, Math.min(window.innerHeight, rect.bottom) - visibleTop)
      }, origin)
    },
    getIframeOrigin () {
      try {
        return new URL(this.iframeUrl, window.location.href).origin
      } catch (error) {
        return null
      }
    },
    getBaseHeight () {
      const header = document.getElementById('header')
      const headerHeight = header ? header.getBoundingClientRect().height : 100
      const availableHeight = window.screen && window.screen.availHeight
        ? window.screen.availHeight
        : window.innerHeight

      return Math.max(1, Math.ceil(availableHeight - headerHeight))
    },
    initializeIframe (event) {
      if (!event || event.type === 'load') this.authCheckVersion++
      const iframe = this.$refs.platformApplicationFrame
      const iframeOrigin = this.getIframeOrigin()

      if (!iframe || !iframe.contentWindow || !iframeOrigin) {
        return
      }

      const baseHeight = this.getBaseHeight()
      this.frameHeight = Math.max(baseHeight, this.frameHeight || 0)
      iframe.contentWindow.postMessage({ type: 'darp:embed-init', baseHeight }, iframeOrigin)
      this.scheduleViewport()
    },
    handleIframeMessage (event) {
      const iframe = this.$refs.platformApplicationFrame
      const message = event.data
      const iframeOrigin = this.getIframeOrigin()

      if (!iframe || event.source !== iframe.contentWindow || event.origin !== iframeOrigin ||
        !message) {
        return
      }

      // DARP 안에서 화면을 옮기면 iframe 은 스스로 스크롤하지 않으므로 포탈을 맨 위로 올린다.
      if (message.type === 'darp:scroll-top') {
        window.scrollTo(0, 0)
        return
      }

      if (message.type === 'darp:auth-check') {
        const version = ++this.authCheckVersion
        const iframeUrl = this.iframeUrl
        // 로그인 복귀로 iframe이 바뀌면 이전 조회 결과로 새 세션을 종료하지 않는다.
        const reply = authenticated => {
          if (version === this.authCheckVersion && this.iframeUrl === iframeUrl &&
            this.$refs.platformApplicationFrame === iframe) {
            event.source.postMessage({ type: 'darp:auth-status', authenticated }, iframeOrigin)
          }
        }
        return portalAuthClient.get('/web/api/member/checkauth')
          .then(response => {
            if (response.status === 200) reply(true)
          })
          .catch(error => {
            if (error.response && error.response.status === 404) reply(false)
          })
      }

      if (message.type !== 'darp:content-height') {
        return
      }

      const height = Number(message.height)
      if (!Number.isFinite(height) || height <= 0 || height > 100000) {
        return
      }

      this.frameHeight = Math.max(this.getBaseHeight(), Math.ceil(height))
      this.scheduleViewport()
    }
  }
}
</script>

<style scoped>
.platform-application-page {
  background: #fff;
  padding-top: 90px;
}
.platform-application-frame-wrap {
  overflow: hidden;
  width: 100%;
}
#platformApplicationFrame {
  display: block;
  min-height: calc(100vh - 90px);
  width: 100%;
}
@media screen and (max-width: 1100px) {
  .platform-application-page {
    padding-top: 60px;
  }
  #platformApplicationFrame {
    min-height: calc(100vh - 60px);
  }
}
</style>

<style>
.platform-application-page + #footer {
  background-image: none;
  padding-top: 30px;
}
</style>



// WEBPACK FOOTER //
// src/components/PlatformApplication.vue